import { apiFetch } from '@crowdex/host-kit/renderer/http';
import type { SourceControlRepositoriesData } from '@crowdex/host-kit/renderer/repository-types';
import { Input } from '@crowdex/ui/components/input';
import { GitBranch, Lock, Plus, X } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import {
  SettingRow,
  SettingSelect,
  SettingsGroup,
  SettingToggle,
  SettingValue,
  settingButtonClass,
} from '@/components/settings-page/setting-controls';
import { errorMessage } from '@/lib/error-message';
import { useRouter } from '@/lib/navigation';
import { pluginPagePath, pluginsPath } from '@/lib/plugins/plugin-sections';

type PolicySource =
  | 'codex'
  | 'claude-code'
  | 'connector-services'
  | 'mcp-registry'
  | 'acp-registry';
type Policy = {
  forbiddenSources: PolicySource[];
  requiredMarketplaces: Array<{
    format: 'codex' | 'claude-code';
    repository: string;
    displayName: string;
  }>;
  memberMarketplaces: boolean;
};
type PolicyState = { policy: Policy; canEdit: boolean };

const POLICY_SOURCE_LABELS: Record<PolicySource, string> = {
  codex: 'Codex marketplaces',
  'claude-code': 'Claude Code marketplaces',
  'connector-services': 'Services',
  'mcp-registry': 'MCP Registry',
  'acp-registry': 'ACP Registry',
};

/** A code forge's mark: the forge glyph in ink, never initials (GitHub and GitLab both "GI"). */
function ForgeMark() {
  return (
    <span aria-hidden className="flex size-7 items-center justify-center text-foreground">
      <GitBranch className="size-4" />
    </span>
  );
}

/** How many repositories Integrations lists before "And N more". */
const REPOSITORIES_SHOWN = 8;

/**
 * The user's code forges, all active at once: each a plugin, connected to an account or not set
 * up, with the hosts it serves, on or off on its own; and for a host several serve, the forge its
 * repositories go to.
 */
function CodeForges() {
  const router = useRouter();
  const [data, setData] = useState<SourceControlRepositoriesData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(
    () =>
      apiFetch<SourceControlRepositoriesData>('/api/source-control/repositories', {
        credentials: 'include',
        cache: 'no-store',
      }).then(setData, (cause: unknown) => setError(errorMessage(cause))),
    [],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const change = (body: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    void apiFetch('/api/source-control/forges', {
      method: 'PUT',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(load, (cause: unknown) => setError(errorMessage(cause)))
      .finally(() => setBusy(false));
  };
  const titleOf = (name: string) =>
    data?.providers.find((provider) => provider.name === name)?.title ?? name;
  return (
    <>
      <SettingsGroup title="Code forges">
        {error && <p className="px-4 py-3 text-destructive text-sm">{error}</p>}
        {data === null && error === null && (
          <p className="px-4 py-3.5 text-muted-foreground text-sm">Checking…</p>
        )}
        {data?.providers.map((provider) => {
          const count = data.repositories.filter(
            (repository) => repository.provider === provider.name,
          ).length;
          return (
            <SettingRow
              description={[
                ...(provider.hosts ? [provider.hosts.join(', ')] : []),
                provider.connected
                  ? `${count} ${count === 1 ? 'repository' : 'repositories'}`
                  : 'Not set up',
              ].join(' · ')}
              key={provider.name}
              leading={<ForgeMark />}
              title={provider.title}
            >
              {!provider.connected && (
                <button
                  className={settingButtonClass()}
                  onClick={() => router.push(pluginPagePath('installed', provider.name))}
                  type="button"
                >
                  Set up
                </button>
              )}
              <SettingToggle
                checked={provider.on !== false}
                disabled={busy}
                label={`Use ${provider.title}`}
                onChange={(checked) => change({ plugin: provider.name, on: checked })}
              />
            </SettingRow>
          );
        })}
        {data !== null && data.providers.length === 0 && (
          <SettingRow description="GitHub, GitLab… each is a plugin" title="No code forge yet">
            <button
              className={settingButtonClass()}
              onClick={() => router.push(pluginsPath())}
              type="button"
            >
              Browse plugins
            </button>
          </SettingRow>
        )}
      </SettingsGroup>
      {data?.shared !== undefined && data.shared.length > 0 && (
        <SettingsGroup title="Hosts">
          {data.shared.map((shared) => (
            <SettingRow
              key={shared.host}
              title={<span className="font-mono text-xs">{shared.host}</span>}
            >
              <SettingSelect
                disabled={busy}
                label={`Forge for ${shared.host}`}
                onValueChange={(plugin) => change({ host: shared.host, plugin })}
                options={shared.forges.map((forge) => ({ value: forge, label: titleOf(forge) }))}
                value={shared.chosen}
              />
            </SettingRow>
          ))}
        </SettingsGroup>
      )}
      {data?.connected && (
        <SettingsGroup title={`Available repositories · ${data.repositories.length}`}>
          {data.repositories.slice(0, REPOSITORIES_SHOWN).map((repository) => (
            <SettingRow
              description={`${repository.host} · ${repository.private ? 'Private' : 'Public'} · ${repository.defaultBranch}`}
              key={`${repository.host}/${repository.fullName}`}
              title={<span className="font-mono text-xs">{repository.fullName}</span>}
            />
          ))}
          {data.repositories.length > REPOSITORIES_SHOWN && (
            <p className="px-4 py-2.5 text-muted-foreground text-xs">
              And {data.repositories.length - REPOSITORIES_SHOWN} more.
            </p>
          )}
          {data.repositories.length === 0 && (
            <p className="px-4 py-3.5 text-muted-foreground text-sm">No repository yet.</p>
          )}
        </SettingsGroup>
      )}
    </>
  );
}

/**
 * Settings › Workspace › Integrations (D44, phone 41): code forges and the organization's plugin
 * sources. Admins change them; members see them locked.
 */
export function IntegrationsSettingsSection() {
  const [state, setState] = useState<PolicyState | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<PolicyState>('/api/organizations/plugin-policy', { credentials: 'include' })
      .then(setState, () => setState(null))
      .finally(() => setLoaded(true));
  }, []);

  async function save(next: Policy) {
    setBusy(true);
    setError(null);
    try {
      setState(
        await apiFetch<PolicyState>('/api/organizations/plugin-policy', {
          method: 'PUT',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            forbiddenSources: next.forbiddenSources,
            requiredMarketplaces: next.requiredMarketplaces.map((entry) => entry.repository),
            memberMarketplaces: next.memberMarketplaces,
          }),
        }),
      );
      return true;
    } catch (cause) {
      setError(errorMessage(cause));
      return false;
    } finally {
      setBusy(false);
    }
  }

  const policy = state?.policy;
  const canEdit = state?.canEdit === true;
  const add = (event: FormEvent) => {
    event.preventDefault();
    const repository = url.trim();
    if (!policy || !repository) return;
    void save({
      ...policy,
      requiredMarketplaces: [
        ...policy.requiredMarketplaces,
        { format: 'codex', repository, displayName: repository },
      ],
    }).then((saved) => {
      if (!saved) return;
      setUrl('');
      setAdding(false);
    });
  };
  const lock = !canEdit && (
    <Lock aria-label="Admins only" className="size-3.5 text-muted-foreground" />
  );

  return (
    <>
      <CodeForges />
      {loaded && policy === undefined && (
        <SettingsGroup title="Plugin sources">
          <SettingRow
            description="Plugin sources are an organization's"
            leading={<Lock aria-hidden className="size-4 text-muted-foreground" />}
            title="Not in an organization"
          />
        </SettingsGroup>
      )}
      {policy !== undefined && (
        <>
          <SettingsGroup aside={lock} title="Plugin sources">
            <SettingRow
              description="Otherwise only the ones below"
              title="Members may add marketplaces"
            >
              <SettingToggle
                checked={policy.memberMarketplaces}
                disabled={!canEdit || busy}
                label="Members may add marketplaces"
                onChange={(checked) => void save({ ...policy, memberMarketplaces: checked })}
              />
            </SettingRow>
            <SettingRow title="Crowdex catalog">
              <SettingValue>Every member gets it</SettingValue>
            </SettingRow>
            {policy.requiredMarketplaces.map((marketplace) => (
              <SettingRow
                description={marketplace.repository.replace(/^https:\/\//, '')}
                key={marketplace.repository}
                title={marketplace.displayName.replace(/^https:\/\/github\.com\//, '')}
              >
                <SettingValue tone="attention">Required</SettingValue>
                {canEdit && (
                  <button
                    aria-label={`Remove ${marketplace.displayName}`}
                    className="flex size-7 items-center justify-center rounded-control text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    disabled={busy}
                    onClick={() =>
                      void save({
                        ...policy,
                        requiredMarketplaces: policy.requiredMarketplaces.filter(
                          (entry) => entry.repository !== marketplace.repository,
                        ),
                      })
                    }
                    type="button"
                  >
                    <X className="size-3.5" />
                  </button>
                )}
              </SettingRow>
            ))}
            {canEdit &&
              (adding ? (
                <form className="flex items-center gap-2 px-4 py-2.5" onSubmit={add}>
                  <Input
                    aria-label="Required marketplace repository URL"
                    autoFocus
                    className="h-9 min-w-0 flex-1 font-mono text-xs"
                    disabled={busy}
                    onChange={(event) => setUrl(event.target.value)}
                    placeholder="https://github.com/owner/repository"
                    value={url}
                  />
                  <button
                    className={settingButtonClass('primary')}
                    disabled={busy || url.trim() === ''}
                    type="submit"
                  >
                    Add
                  </button>
                </form>
              ) : (
                <SettingRow title="Add a required marketplace">
                  <button
                    className={settingButtonClass()}
                    onClick={() => setAdding(true)}
                    type="button"
                  >
                    <Plus aria-hidden className="size-3.5" /> Add
                  </button>
                </SettingRow>
              ))}
          </SettingsGroup>
          <SettingsGroup aside={lock} title="Catalog sources">
            {(Object.keys(POLICY_SOURCE_LABELS) as PolicySource[]).map((source) => {
              const allowed = !policy.forbiddenSources.includes(source);
              return (
                <SettingRow key={source} title={POLICY_SOURCE_LABELS[source]}>
                  <SettingToggle
                    checked={allowed}
                    disabled={!canEdit || busy}
                    label={`${allowed ? 'Forbid' : 'Allow'} ${POLICY_SOURCE_LABELS[source]}`}
                    onChange={(checked) =>
                      void save({
                        ...policy,
                        forbiddenSources: checked
                          ? policy.forbiddenSources.filter((entry) => entry !== source)
                          : [...policy.forbiddenSources, source],
                      })
                    }
                  />
                </SettingRow>
              );
            })}
          </SettingsGroup>
          {error && <p className="-mt-4 mb-6 text-destructive text-sm">{error}</p>}
        </>
      )}
    </>
  );
}
